import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'

import Anchors from '../../components/Anchors'
import { AuthorAvatar } from '../../components/AuthorAvatar'
import Block from '../../components/Block'
import Body from '../../components/Body'
import { BlogAuthorInfo } from '../../components/content-management/blog/BlogAuthorInfo'
import { BlogPostCard } from '../../components/content-management/blog/BlogPostCard'
import RichText from '../../components/content-management/RichText'
import CTAMain from '../../components/ctas/CTAMain'
import Divider from '../../components/Divider'
import Footer from '../../components/Footer'
import Layout from '../../components/Layout'
import { Link } from '../../components/Link'
import ReadingProgressBar from '../../components/ReadingProgressBar'
import { Schema } from '../../components/Schema'
import ScrollToTop from '../../components/ScrollToTop'
import { SearchResultsBlock } from '../../components/Search'
import SEO from '../../components/SEO'
import SocialSharing from '../../components/SocialSharing'
import VerticalTextDivider from '../../components/VerticalTextDivider'
import { breakpoints } from '../../constants/breakPoints'
import { blogBreakPoint, longCopyBreakPoint } from '../../constants/breakPoints'
import { getMappedBlogPosts, getReadTime } from '../../helpers/blog'
import { isMobile } from '../../helpers/common'
import { enhanceContentObject, getAnchors, richTextRenderingOptions } from '../../helpers/contentful'
import { getCtaMainType, getCtaSidebar } from '../../helpers/cta'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useHsFormCallback } from '../../hooks/useHsFormCallback'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { Props } from '../../interfaces/blog'
import { BlogHeader, firstPostLinkStyle } from '../../pages/blog'
import { sidebarStyle } from '../learn/LearnArticle'
import { longCopyBlockStyle } from '../resources/GuidePage'

export const BlogPostPageTemplate: React.FunctionComponent<Props> = ({
  data: {
    blogPost: {
      content,
      publishedAt,
      createdAt,
      updatedAt,
      slug,
      title,
      abstract: { abstract },
      heroImage,
      imageWithBorder,
      postCategory: { name: categoryName, slug: categorySlug, categoryColor },
      postAuthor,
      funnelStage,
    },
    blogPosts: allBlogPosts,
  },
}) => {
  const blogPosts = React.useMemo(() => getMappedBlogPosts(allBlogPosts), [allBlogPosts])
  const json = React.useMemo(() => JSON.parse(content.raw), [content])
  const anchors = React.useMemo(() => getAnchors(json), [content])
  const wordCount = React.useMemo(() => documentToPlainTextString(json).split(' ').length, [])
  const readTime = getReadTime(wordCount)
  const postDate = dayjs(publishedAt || createdAt).format('D MMM YYYY')

  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const filteredPosts = React.useMemo(
    () => blogPosts.filter(({ title }) => title.toLowerCase().includes(debouncedSearch.toLowerCase())),
    [debouncedSearch]
  )

  // addding funnelStage and categorySlug to content object
  const postContent = enhanceContentObject(content, funnelStage, categorySlug)

  const setBlogSubscribed = useLocalStorage('blog_subscribed', false)[1]
  useHsFormCallback({
    onFormSubmittedCallback: () => setBlogSubscribed(true),
    hubspotFormIds: ['7cd8c2f7-7dc1-498c-9184-29539d228229'],
  })

  return (
    <Layout whiteBG>
      <SEO title={title} description={abstract} imageSrc={`https:${heroImage.file.url}`} lang="en" />
      <Schema
        type="BlogPosting"
        headline={title}
        url={`https://airfocus.com/blog/${slug}/`}
        datePublished={publishedAt}
        dateCreated={createdAt}
        dateModified={updatedAt}
        abstract={abstract}
        author={postAuthor.name}
        timeRequired={readTime}
      />
      <BlogHeader search={search} setSearch={setSearch} hideOnScroll />
      <Body>
        <ReadingProgressBar />
        <ScrollToTop />
        <div css={headingContainerStyle}>
          <Link
            className="heading4 center opacity-on-hover"
            href={`/blog/category/${categorySlug}/`}
            css={firstPostLinkStyle(categoryColor)}
          >
            {categoryName}
          </Link>
          <h1 className="heading1 center">{title}</h1>
          <div className="subtitle" css={subtitleStyle}>
            <span>{postDate}</span>
            <VerticalTextDivider height="23px" marginLeftRight="1rem" translateY="0" />
            <span>{readTime}</span>
            <VerticalTextDivider className="hide-on-small" height="23px" marginLeftRight="1rem" translateY="0" />
            <BlogAuthorInfo
              className="hide-on-small"
              author={postAuthor}
              textClassName="subtitle"
              avatarSize={43}
              isImageOnTheRight
            />
          </div>
        </div>
        <GatsbyImage
          className={imageWithBorder ? 'with-box-shadow' : ''}
          image={heroImage.gatsbyImageData}
          alt={title}
          css={imageStyle}
          loading="eager"
        />
        <BlogAuthorInfo
          className="hide-on-medium-large-desktop"
          author={postAuthor}
          textClassName="subtitle"
          avatarSize={43}
          cssStyles={bottomAuthorStyle}
        />

        <Block css={longCopyBlockStyle}>
          {anchors.length === 0 ? (
            <h3 className="heading3 no-margin hide-on-small-medium" css={anchorsFallbackStyle}>
              {title}
            </h3>
          ) : (
            <Anchors anchors={anchors} />
          )}
          <div>
            <RichText>{renderRichText(postContent, richTextRenderingOptions)}</RichText>
            <Divider size={2} notDecreased />
            {isMobile(longCopyBreakPoint) && (
              <>
                <AuthorAvatar blackBG={false} withDescription leftOnSmall author={postAuthor} />
                <Divider size={2} />
              </>
            )}
            <SocialSharing title={title} />
          </div>
          {!isMobile(longCopyBreakPoint) && (
            <div css={sidebarStyle}>
              <AuthorAvatar blackBG={false} author={postAuthor} withDescription isInSidebar />
              <Divider size={6.5} color="rgba(136, 146, 158, 0.4)" />
              {getCtaSidebar(funnelStage, categorySlug)}
            </div>
          )}
        </Block>

        <div css={readAlsoContainerStyle}>
          <h3 className="heading3">Read also</h3>
          <Block columns={3} rowGap={2} twoColumnsForTablet css={readAlsoBlockStyle} alignContent="stretch">
            {blogPosts.slice(0, 4).map((blogPost, index) => (
              <BlogPostCard
                className={index === 3 ? 'hide-on-small hide-on-large-desktop' : undefined}
                key={blogPost.id}
                {...blogPost}
              />
            ))}
          </Block>
        </div>

        <Divider size={4} color="transparent" />

        <CTAMain instantTourVersion type={getCtaMainType(categorySlug)} />

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          search={search}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredPosts}
          resultsBlockContent={filteredPosts.map((filteredPost) => (
            <BlogPostCard key={filteredPost.id} {...filteredPost} />
          ))}
          breakPoint={blogBreakPoint}
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default BlogPostPageTemplate

export const query = graphql`
  query ($id: String!) {
    blogPost: contentfulBlogPost(id: { eq: $id }) {
      id
      slug
      title
      postCategory {
        name
        slug
        categoryColor
      }
      postAuthor {
        name
        jobTitle
        companyName
        description {
          description
        }
        slug
        authorPhoto {
          gatsbyImageData(width: 80, quality: 90)
        }
        authorPublications: blog_post {
          id
        }
      }
      abstract {
        abstract
      }
      heroImage {
        gatsbyImageData(layout: FULL_WIDTH, quality: 90)
        file {
          url
        }
      }
      imageWithBorder
      funnelStage
      content {
        raw
        references {
          ... on ContentfulXTwitterTikTokRedditPost {
            contentful_id
            __typename
            htmlCode {
              htmlCode
            }
          }
          ... on ContentfulAsset {
            # contentful_id and __typename are required to resolve the references
            contentful_id
            __typename
            title
            gatsbyImageData(width: 800, quality: 90)
          }
          ... on ContentfulDefinitionCard {
            id
            __typename
            contentful_id
            title
            description {
              description
            }
          }
          ... on ContentfulCrossSellingEntry {
            id
            type
            ebookResource {
              slug
            }
            contentful_id
            __typename
            title
          }
          ... on ContentfulTestimonialQuote {
            __typename
            contentful_id
            name
            position
            quote {
              quote
            }
            avatar {
              gatsbyImageData(width: 200, quality: 90)
            }
          }
          ... on Node {
            ... on ContentfulImageWithLink {
              id
              contentful_id
              __typename
              link
              image {
                title
                gatsbyImageData(width: 800, quality: 90)
              }
              imageDescription: childContentfulImageWithLinkDescriptionTextNode {
                description
              }
            }
          }
        }
      }
      createdAt
      publishedAt
      updatedAt
    }

    blogPosts: allContentfulBlogPost(sort: { fields: createdAt, order: DESC }, filter: { id: { ne: $id } }) {
      edges {
        node {
          id
          slug
          title
          postAuthor {
            name
            slug
            authorPhoto {
              gatsbyImageData(width: 35, quality: 90)
            }
          }
          postCategory {
            name
            slug
            categoryColor
          }
          abstract {
            abstract
          }
          heroImage {
            gatsbyImageData(width: 400, quality: 90)
          }
          imageWithBorder
          createdAt
          publishedAt
        }
      }
    }
  }
`

export const subTitleContainerStyle = css`
  margin-bottom: 3em;
`

export const blogTitleStyle = css`
  font-weight: normal;
  font-size: 1.75em;
  text-align: center;
  @media screen and (min-width: ${breakpoints.medium}px) {
    text-align: left;
    font-size: 42px;
  }
`

export const leftColumnSecondRowContainerStyle = css`
  grid-row: 2;
  display: none;
  @media screen and (min-width: ${breakpoints.xlarge}px) {
    display: block;
  }
`

export const blogPostContainerStyle = css`
  margin: 75px auto 150px auto;
  display: grid;
  grid-gap: 0px 10px;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  padding: 0 20px;
  text-align: justify;

  @media screen and (min-width: ${breakpoints.xlarge}px) {
    text-align: left;
    grid-gap: 0px 40px;
    grid-template-columns: auto minmax(auto, 768px);
    grid-template-rows: auto 1fr;
  }
  @media screen and (min-width: ${breakpoints.xxlarge}px) {
    padding: 0 30px;
    grid-gap: 0px 40px;
    grid-template-columns: 1fr minmax(auto, 768px) 1fr;
  }
`

// export for glossaryTerm & learnArticle
export const anchorsFallbackStyle = css`
  position: sticky;
  top: 6.5rem;
`

const readAlsoBlockStyle = css`
  margin-bottom: 4rem;
`

const imageStyle = css`
  max-height: 400px;
  width: 100%;
  margin-bottom: 4rem;
  border-radius: 0.5rem;

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-bottom: 1.5rem;
  }
`

const bottomAuthorStyle = css`
  & > div {
    margin-bottom: 2rem;
  }
`

const subtitleStyle = css`
  display: flex;
  text-align: left;
  align-items: center;
  justify-content: center;
  margin: 0 0 3rem !important;
  @media screen and (max-width: ${breakpoints.large}px) {
    margin-bottom: 2rem !important;
  }
`

const headingContainerStyle = css`
  padding-top: 4rem;
  text-align: center;

  .heading1 {
    margin: 0.5rem auto 2rem;
    pointer-events: none;
  }

  @media screen and (max-width: ${breakpoints.large}px) {
    padding-top: 3rem;

    .heading1 {
      margin-bottom: 1rem;
    }
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    .heading1 {
      font-size: 40px !important;
      line-height: 42.5px !important;
    }
  }
`

// export for AcademyResource
export const readAlsoContainerStyle = css`
  margin: 4rem 0;

  .heading3 {
    margin-bottom: 2rem;
  }
`
